/**
 * Configures MUI v5's `v5-` class name prefix, and does it early enough to
 * matter. Import this before anything that pulls in `@mui/material`.
 *
 * `@backstage/theme` prefixes every MUI v5 class name with `v5-` so it cannot
 * collide with the MUI v4 JSS class names Backstage still emits. It does that
 * from a module-level `unstable_ClassNameGenerator.configure()` call, which
 * means the prefix only reaches component modules that are evaluated *after*
 * `@backstage/theme` itself.
 *
 * `@mui/material/Grid` loses that race. `gridClasses` is built at module scope,
 * and Grid bakes it into a static child selector for its `spacing` styles:
 *
 *     .css-<hash> { margin-top: -24px; margin-left: -24px; }
 *     .css-<hash> > .MuiGrid-item { padding-top: 24px; padding-left: 24px; }
 *
 * Evaluate that before the generator is configured and the selector keeps the
 * bare `.MuiGrid-item`, while the items themselves render as `v5-MuiGrid-item`.
 * The container still gets its negative margins, the items never get the
 * padding that is supposed to cancel them, and every `<Grid container
 * spacing={n}>` ends up shifted `n * 8px` up and to the left and `n * 8px` too
 * wide. On the home page that slid the cards underneath the sidebar and clipped
 * the page title against the top of the viewport.
 *
 * Configuring the generator from the first import in `index.tsx` wins the race
 * for every MUI module. This is the same call `@backstage/theme` makes, and it
 * is idempotent — the prefix is only applied to names that do not already carry
 * it — so the theme's own call later on is a no-op.
 */
import { unstable_ClassNameGenerator } from '@mui/material/className';

unstable_ClassNameGenerator.configure(componentName =>
  (componentName ?? '').startsWith('v5-')
    ? componentName
    : `v5-${componentName}`,
);
